/**
 * Things this reader has already been shown, and must not be shown again.
 *
 * Two of them today and they are the same shape, which is why they are one
 * module: the splash's handover, remembered for the SESSION, and the deck's
 * swipe hint, remembered for the DEVICE. Both exist because the alternative is a
 * product that repeats its introduction — an animated splash on every navigation
 * back to `/en`, a "swipe me" label on every one of twelve cards — and a
 * repeated introduction is a nag however good the animation is.
 *
 * The two lifetimes are the whole design, not an implementation detail:
 *
 *   SESSION  the splash is a brand moment for an arrival. Somebody who opens the
 *            app tomorrow is arriving again and should get it again; somebody who
 *            taps back to the front door thirty seconds later is not.
 *   DEVICE   the swipe hint teaches a GESTURE. A gesture is learned once and
 *            stays learned, so re-teaching it next week would be the app failing
 *            to notice it had already been understood.
 *
 * Every entry point takes its storage as a DEFAULTED PARAMETER, per the repo's
 * rule for browser APIs — and here that rule earns its keep twice over, because
 * the accessor ITSELF throws in Safari's private mode rather than returning
 * null. An unguarded `window.sessionStorage` on this path would take the splash
 * down on first paint, which is the one screen with nothing behind it.
 *
 * Nothing stored here is sensitive and nothing is worth syncing: these are two
 * booleans about what a reader has looked at (CON-003).
 */

const NAMESPACE = 'legends';

function namespacedKey(key) {
    return `${NAMESPACE}:${key}`;
}

/**
 * The two stores, each guarded, each returning null rather than throwing.
 *
 * Exported as functions rather than as the objects themselves so that a caller
 * naming one as a default parameter resolves it at CALL time. Naming the object
 * would evaluate it at module load, which in a private-mode Safari is exactly
 * the throw this file exists to contain, moved earlier.
 */
export function sessionMemory(windowObject = typeof window === 'undefined' ? null : window) {
    try {
        return windowObject?.sessionStorage ?? null;
    } catch (error) {
        return null;
    }
}

export function deviceMemory(windowObject = typeof window === 'undefined' ? null : window) {
    try {
        return windowObject?.localStorage ?? null;
    } catch (error) {
        return null;
    }
}

/** The splash has handed over once already in this browsing session. */
export const SPLASH_HANDED_OVER = 'splash.handedOver';

/** This device has been told the deck can be swiped. */
export const DECK_HINT_SEEN = 'cars.deckHintSeen';

export function hasSeen(key, storage) {
    if (!storage) {
        return false;
    }

    try {
        return storage.getItem(namespacedKey(key)) === 'true';
    } catch (error) {
        // A read that throws is a reader we cannot recognise, which is the same
        // situation as a first visit — so it is answered the same way. Showing
        // the splash twice is recoverable; a thrown error during render is not.
        return false;
    }
}

export function rememberSeen(key, storage) {
    if (!storage) {
        return false;
    }

    try {
        storage.setItem(namespacedKey(key), 'true');
        return true;
    } catch (error) {
        // Quota exhausted or storage disabled. A lost preference costs the
        // reader one repeat; a throw costs them the screen.
        return false;
    }
}
